// AP-MED-05 — مشترک خدمات تکمیلی تامین (US-MED-095، 096، 097؛ US-INS-094): خدمات ارجاعی و انجام توسط پزشک دوم،
// کارتابل پرستاری و نشان بیماری خاص. متن‌ها فقط از فضای نام med؛ نام بیماری و نام خدمت داده Clinical‌اند و هرگز در
// عنوان صفحه یا پیامک نمی‌آیند.
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { MedNationalSubmissionStatus } from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { SampleBadge, StatusChip, type StatusTone } from '@phoenix/ui';

/** کارتابل پرستاری تامین (US-MED-096). */
export const SCR_STAFF_715 = 'SCR-STAFF-715';

export const NURSING_CARTABLE_PATH = '/desk/nursing-cartable';

export const supplementaryKeys = {
  cartable: (tenantId: string | undefined, patientId: string, doctorNationalId: string) =>
    ['medorders', tenantId, 'nursing-cartable', patientId, doctorNationalId] as const,
  diseases: (tenantId: string | undefined, patientId: string) => ['medorders', tenantId, 'flag-diseases', patientId] as const,
  flags: (tenantId: string | undefined, patientId: string) => ['medorders', tenantId, 'insurer-flags', patientId] as const,
};

/** سطح حمایت مستند §۴.۱۱ (۱ نیازمند حمایت، ۲ حمایت زیاد، ۳ حمایت بسیار زیاد). */
export const SUPPORT_LEVELS: readonly number[] = [1, 2, 3];

/** رنگ وضعیت ثبت یک ردیف صف تکمیلی (همان دامنه وضعیت ارسال ملی). */
export function supplementaryTone(status: MedNationalSubmissionStatus): StatusTone {
  switch (status) {
    case 'Registered':
      return 'done';
    case 'Failed':
      return 'error';
    case 'Deleted':
      return 'cancelled';
    default:
      // `Pending` و `AwaitingProviderAction` هر دو «در انتظار»اند؛ علت انتظار در ستون خطا دیده می‌شود.
      return 'pending';
  }
}

/**
 * نشان «نمونه» نتیجه Adapter نمونه (CLAUDE.md §۱۰). از <see cref="SampleBadge"/> مشترک استفاده می‌کند تا متن و نشان
 * همه جای محصول یکی بماند، و فقط شرط نمایش را می‌گیرد.
 */
export function SupplementarySampleBadge({ isSample }: { isSample: boolean | null | undefined }) {
  return isSample ? <SampleBadge /> : null;
}

/** وضعیت ردیف صف با متن i18n. */
export function SupplementaryStatus({ status }: { status: MedNationalSubmissionStatus }) {
  return <StatusChip tone={supplementaryTone(status)} labelKey={`med:supplementary_status.${status}`} />;
}

/**
 * راهنمای «این سرویس روی اتصال بیمه‌گر نیست» (BR-INT-091؛ PHX-MED-0339): نبود قابلیت هرگز به فرض «پشتیبانی می‌شود»
 * تبدیل نمی‌شود و UI به‌جای تلاش، علت را می‌گوید.
 */
export function UnsupportedNotice({ serviceKey }: { serviceKey: string }) {
  const { t } = useTranslation('med');
  return (
    <Alert severity="info" data-testid="med-supplementary-unsupported">
      <Stack spacing={0.5}>
        <Typography variant="body2">{t('med:supplementary.unsupported')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t(`med:supplementary.service.${serviceKey}`)}
        </Typography>
      </Stack>
    </Alert>
  );
}
